import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, NavigationStart, Route, Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { NgSelectConfig } from '@ng-select/ng-select';
import { lastValueFrom, Subscription } from 'rxjs';
import { compiledVersion as compiledVersionData } from '../compiled-version';
import { AppRouteData, RouteValue } from './app-route-data';
import { desktopOnly } from './app-routing.module';
import { BackButtonService } from './back-button.service';
import { FirebaseService } from './firebase.service';
import { LocalStorage } from './local-storage.class';
import { Routenames } from './route-names.enum';
import { RouteParamnames } from './route-param-names.enum';
import { AuthService } from './services/auth/auth.service';
import { AuthorisationService } from './services/auth/authorisation.service';
import { Features } from './services/auth/features';
import { Roles } from './services/auth/roles';
import { CustomerProjectService } from './services/customer-project/customer-project.service';
import { CustomerService } from './services/customer/customer.service';
import { RouteParamsService } from './services/route-params.service';
import { SearchService } from './services/search/search.service';
import { Settings } from './settings.class';
import { User } from './user.interface';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
    public copyDate = new Date().getFullYear();
    public user: User;
    public parentUser: User;
    public version = compiledVersionData || {};

    routeTitle = '';
    Settings = Settings;
    Routenames = Routenames;
    RouteParamnames = RouteParamnames;
    showBackButton = false;
    hideHeader = false;
    hideSearch = false;
    hideMenuContent = false;
    showSearchMobile = false;
    bottomMenu: MenuItem[] = [];
    escapeCount = 0;
    customerMap = new Map<number, string>();
    projectMap = new Map<number, string>();
    search = '';
    fcSearch = new FormControl();

    @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;
    @ViewChild('banners') private banners?: ElementRef<HTMLElement>;

    private subscriptions = new Subscription();
    private firebaseSubscribed = false;
    private tokenRefreshPending = false;
    private bannersResizeObserver?: ResizeObserver;

    constructor(
        private router: Router,
        private ngZone: NgZone,
        private authService: AuthService,
        private backButtonService: BackButtonService,
        private firebaseService: FirebaseService,
        private customerService: CustomerService,
        public searchService: SearchService,
        private customerProjectService: CustomerProjectService,
        private route: ActivatedRoute,
        public routeParams: RouteParamsService,
        private ngSelectConfig: NgSelectConfig
    ) {
        this.subscriptions.add(
            this.fcSearch.valueChanges.subscribe(searchValue =>
                this.searchService.update(searchValue?.toLowerCase().trim())
            )
        );

        this.ngSelectConfig.notFoundText = 'Geen items gevonden';
        this.ngSelectConfig.typeToSearchText = 'Typ om te zoeken';
        this.ngSelectConfig.loadingText = 'Aan het laden...';
        this.ngSelectConfig.clearAllText = 'Verwijder alles';
        this.ngSelectConfig.addTagText = 'Voeg item toe';
    }

    @HostListener('window:keydown', ['$event'])
    onKeyDown(event: KeyboardEvent) {
        if (event.getModifierState?.('Control') && event.key === 'f' && !this.hideSearch) {
            this.searchInput?.nativeElement.focus();
            event.preventDefault();
        }
        if (event.key === 'Escape') {
            if (this.escapeCount === 1) {
                this.escapeCount = 0;
                if (this.showBackButton) {
                    this.navigateBack();
                }
            } else {
                this.escapeCount++;
                setTimeout(() => (this.escapeCount = 0), 300);
            }
        }
    }

    ngOnInit() {
        LocalStorage.version = this.version;

        this.user = LocalStorage.getUser();
        this.parentUser = LocalStorage.getParentUser();
        if (LocalStorage.getUser()) {
            this.checkToken();
            this.setFirebaseSubs();
            this.loadNavigationNames();
        }
        this.setMenu();

        this.subscriptions.add(
            this.authService.onLogin.subscribe(loggedIn => {
                this.user = LocalStorage.getUser();
                this.parentUser = LocalStorage.getParentUser();
                this.setMenu();
                this.updateRouteUi();
                if (loggedIn) {
                    this.setFirebaseSubs();
                    this.loadNavigationNames();
                }
            })
        );

        this.subscriptions.add(
            this.router.events.subscribe(event => {
                // Zoekterm leegmaken zodra de navigatie begint, dus voordat de volgende pagina
                // wordt opgebouwd. Bij NavigationEnd zou de nieuwe pagina er al staan en een
                // zoekactie krijgen die de gebruiker niet heeft gedaan.
                if (event instanceof NavigationStart) {
                    this.fcSearch.reset(null, { emitEvent: false });
                    this.searchService.clear();
                }
                if (event instanceof NavigationEnd) {
                    if (LocalStorage.getUser()) {
                        this.checkToken();
                    }
                    this.updateRouteUi();
                    this.loadPendingAccessCount();
                }
            })
        );

        App.addListener('appUrlOpen', event => {
            this.ngZone.run(() => {
                const url = new URL(event.url);
                if (url.pathname.length > 1) {
                    this.router.navigateByUrl(`${url.pathname}${url.search}`);
                }
            });
        });

        const restoreData = LocalStorage.getRestoreData();

        console.info('Desktop:', desktopOnly());

        if (!desktopOnly()) {
            if (restoreData && (location.href.endsWith(`/${Routenames.customers}`) || location.href.endsWith('/'))) {
                this.router.navigate([Routenames.customers, restoreData.customerId], {
                    queryParams: { project: restoreData.projectId },
                    replaceUrl: true,
                });
            }
        }
    }

    ngAfterViewInit() {
        if (!this.banners) {
            return;
        }
        const bannersElement = this.banners.nativeElement;
        this.ngZone.runOutsideAngular(() => {
            this.bannersResizeObserver = new ResizeObserver(() => {
                document.documentElement.style.setProperty('--banner-offset', `${bannersElement.offsetHeight}px`);
            });
            this.bannersResizeObserver.observe(bannersElement);
        });
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        this.bannersResizeObserver?.disconnect();
    }

    navigateBack(): void {
        this.backButtonService.navigateBack();
    }

    backToParentUser() {
        this.authService.switchToParent();
    }

    private updateRouteUi() {
        let current: ActivatedRoute | null = this.route;
        let data: AppRouteData = {};
        while (current) {
            Object.assign(data, current.snapshot.data as AppRouteData);
            current = current.firstChild;
        }

        const activeMenuId = this.resolve(data.menuId, data.menu?.id);
        this.bottomMenu.forEach(item => (item.active = item.id === activeMenuId));
        const activeMenuItem = this.bottomMenu.find(item => item.active);
        this.routeTitle = this.resolve(data.title, activeMenuItem?.name ?? '');
        this.hideHeader = data.header === false;
        this.hideSearch = !this.resolve(data.search, true);
        this.hideMenuContent = data.hideMenu === true;
        this.showBackButton = this.backButtonService.canNavigateBack;
    }

    private setMenu() {
        const menu: MenuItem[] = [];
        const visit = (routes: readonly Route[]) => {
            routes.forEach(route => {
                const routeData = route.data as AppRouteData | undefined;
                const menuData = routeData?.menu;
                if (menuData && route.path) {
                    menu.push({
                        id: menuData.id,
                        name: this.resolve(menuData.label, ''),
                        icon: menuData.icon,
                        routerLink: route.path,
                        visible: menuData.visible ?? (() => true),
                        active: false,
                    });
                }
                if (route.children) {
                    visit(route.children);
                }
            });
        };

        visit(this.router.config);
        this.bottomMenu = menu;
        this.loadPendingAccessCount();
    }

    /**
     * Badge on the projects menu item: open access requests across every project the user manages.
     * Refreshed on navigation so accepting a request in the manage screen updates it.
     */
    private loadPendingAccessCount() {
        const item = this.bottomMenu.find(menuItem => menuItem.id === Routenames.projects);
        if (!item || !AuthorisationService.hasFeature(Features.CustomerProjectsPendingAccessCounts)) {
            return;
        }

        this.subscriptions.add(
            this.customerProjectService
                .getPendingAccessCounts()
                .subscribe(counts => (item.badge = counts.total || undefined))
        );
    }

    private resolve<T>(value: RouteValue<T> | undefined, fallback: T): T {
        return value === undefined ? fallback : typeof value === 'function' ? (value as () => T)() : value;
    }

    private loadNavigationNames() {
        this.customerMap.clear();
        this.projectMap.clear();
        if (!AuthorisationService.hasRole(Roles.Consumer)) {
            this.subscriptions.add(
                this.customerService
                    .getList()
                    .subscribe(customers =>
                        customers.forEach(customer => this.customerMap.set(customer.id, customer.name))
                    )
            );
            this.subscriptions.add(
                this.customerProjectService
                    .getProjectnames()
                    .subscribe(projectNames =>
                        projectNames.data.forEach(project => this.projectMap.set(project.id, project.name))
                    )
            );
        } else {
            this.subscriptions.add(
                this.customerProjectService.getConsumerProjects().subscribe(response =>
                    response.data.forEach(project => {
                        this.customerMap.set(project.customer_id, project.customer_name);
                        this.projectMap.set(project.id, project.name);
                    })
                )
            );
        }
    }

    private setFirebaseSubs() {
        if (this.firebaseSubscribed) {
            return;
        }
        this.firebaseSubscribed = true;
        this.subscriptions.add(
            this.firebaseService.newNetification.subscribe(data => {
                if (data?.tap) {
                    this.runNotification(data);
                }
            })
        );
        if (FirebaseService.token) {
            this.authService.updateFirebaseToken(FirebaseService.token).subscribe();
        }
    }

    private runNotification(data: { url: string | null }) {
        const url = data.url;
        if (!url) {
            return;
        }
        this.ngZone.run(() => {
            setTimeout(() => this.router.navigateByUrl(url), Capacitor.getPlatform() === 'ios' ? 250 : 0);
        });
    }

    private checkToken() {
        const refreshNotIfBefore = new Date();
        refreshNotIfBefore.setDate(refreshNotIfBefore.getDate() - 3);
        const isStale = () => new Date(LocalStorage.getTokenDate()) < refreshNotIfBefore;

        // checkToken runs on every navigation; without this guard each one chains another refresh
        if (!isStale() || this.tokenRefreshPending) {
            return;
        }
        this.tokenRefreshPending = true;

        // Re-check inside: another tab may have refreshed while waiting for the lock
        const refresh = async () => {
            try {
                if (isStale()) {
                    // refresh() completes empty while another refresh is in flight
                    await lastValueFrom(this.authService.refresh(), { defaultValue: null });
                }
            } finally {
                this.tokenRefreshPending = false;
            }
        };

        // Lock serializes tabs; unavailable in insecure contexts and old browsers
        setTimeout(
            () => (navigator.locks ? navigator.locks.request('token-refresh', refresh) : refresh()),
            (2 + Math.random() * 3) * 1000
        );
    }
}

interface MenuItem {
    id: string;
    name: string;
    icon: string;
    routerLink: string;
    visible: () => boolean;
    active: boolean;
    badge?: number;
}
